import React, { useEffect, useState } from "react";
import { Button, Iconography, IconographySizes, Badge, Tag, TagAppearance, Link, Loader, LoaderSizes } from "@hid-galaxy-ui/galaxy-react";
import { IconButton, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { PartnerEngagementConfigurationListType } from "../../../../../../redux/slices/partner-program/engagement-configuration/Types";
import { getPartnerEngagementConfigurationListSagaAction, addEnagagementSagaAction, deleteEngagementListSaga, insertEngagementCategorySagaAction, updateEnagagementSagaAction } from "../../../../../../redux/saga/partner-program/engagement-configuration/saga-actions";
import { formatDate } from "../tier-management";
import ViewDescriptionModal from "./viewDescriptionModal";
import AddEditEngagementModal from "./addEngagementModal";
import { AddEngagementDetailsType } from "../../../../../../redux/saga/partner-program/regional-multipliers/saga-actions";
import DeleteModal from "../../../../../../common/components/DeleteModal";

type Props = {
  activeTab: number;
  partnerProgramId: number;
  readOnly?: boolean;
};

const EngagementConfiguration: React.FC<Props> = ({
  activeTab,
  partnerProgramId,
  readOnly = false,
}) => {

  const dispatch = useAppDispatch();


  const [showDescriptionModal, setShowDescription] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteTierId, setDeleteTierId] = React.useState<number | null>(null);
  const [engagementDetails, setEngagementDetails] = useState<AddEngagementDetailsType>({
    activity_name: '',
    category_id: '',
    is_partner_editable: true,
    status: true,
    comments: '',
    description: '',
    accelerator_value: [{
      option_key: '',
      option_value: ''
    }]
  })

  //comment for static data
  const {
    partnerEngagementConfigurationList,
    status: { partnerEngagementConfigurationListLoading, deletePartnerEngagementConfigurationListLoading } } = useAppSelector(
      (state) => state.partner_engagementConfiguration
    );


  // Normalize the list to an array for safe rendering
  const list = Array.isArray(partnerEngagementConfigurationList)
    ? partnerEngagementConfigurationList

    : [];


  React.useEffect(() => {
    if (activeTab === 5) {
      dispatch(getPartnerEngagementConfigurationListSagaAction());
    }
  }, [activeTab, dispatch, partnerProgramId]);


  // const onEditClick = (region: PartnerEngagementConfigurationListType[number]) => {
  //   setEditregion(region);
  //   setShowEdit(true);
  // };

  const onDeleteClick = (id: number) => {
    setDeleteModalOpen(true);
    setDeleteTierId(id);
    //dispatch(deleteEngagementListSaga(id));
    //dispatch(getPartnerEngagementConfigurationListSagaAction());
  };

  const onDeleteEngagement = (id: number) => {

    dispatch(deleteEngagementListSaga(id));
    // dispatch(getPartnerTierListSagaAction(PARTNER_PROGRAM_ID));
  };

  useEffect(() => {
    if (!deletePartnerEngagementConfigurationListLoading) {
      setDeleteModalOpen(false);
    }

  }, [deletePartnerEngagementConfigurationListLoading])


  return (
    <div>
      <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", marginTop: "12px", marginBottom: "12px" }}>
        <div>
          <h1 style={{ fontFamily: "Roboto", fontStyle: "normal", fontWeight: 500, fontSize: "16px", lineHeight: "20px", letterSpacing: "0.2px", color: "#0A0A0A", marginBottom: "5px" }}>Engagement Configuration</h1>
          <Typography variant="h4" style={{ fontFamily: "Roboto", fontStyle: "normal", fontWeight: 400, fontSize: "14px", lineHeight: "18px", letterSpacing: "0.2px", color: "#4A5565" }}>Configure engagement types and point values</Typography>
        </div>
        <Button
          variant="primary"
          label="Add Engagement"
          icon={<Iconography icon="plus" />}
          disabled={readOnly}
          onClick={() => {
            setShowAddModal(true)
            setEngagementDetails({
              activity_name: '',
              category_id: '',
              is_partner_editable: true,
              status: true,
              comments: '',
              description: '',
              accelerator_value: [{
                option_key: '',
                option_value: ''
              }]
            })
          }}
        />
      </div>
      <div className="pp-table" role="table" aria-label="Regional Multipliers">
        <div className="pp-table-header"
          style={{
            gridTemplateColumns: "1fr 1fr 1fr 1fr 1.5fr 1fr",
          }}
          role="rowgroup">
          {/* <div className="pp-th col-expander" role="columnheader" aria-hidden="true" /> */}
          <div className="pp-th col-region-type" role="columnheader">Activity Label</div>
          <div className="pp-th col-count" role="columnheader">Category</div>
          <div className="pp-th col-points" role="columnheader">Description</div>
          <div className="pp-th col-created" role="columnheader">Status</div>
          <div className="pp-th col-created" role="columnheader">Partner Edit Access</div>
          <div className="pp-th col-actions" role="columnheader">Actions</div>
        </div>
        <div className="pp-table-body" role="rowgroup">


          {partnerEngagementConfigurationListLoading ? (
            <div
              style={{
                padding: "16px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Loader variant={LoaderSizes.medium} />
            </div>
          ) : (partnerEngagementConfigurationList ?? []).length === 0 ? (
            <div
              style={{
                padding: "16px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              No Engagements found
            </div>
          ) :


            (
              (partnerEngagementConfigurationList ?? []).map((engagement) => {
                return (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr 1.5fr 1fr",
                      gap: "8px",
                      alignItems: "center",
                      padding: "16px 0px 16px 16px",
                      minHeight: "48px",
                      borderTop: "2px solid #F3F4F6",
                    }}
                    role="row" key={engagement.ID}>
                    <div className="pp-td col-region-type" role="cell">
                      {engagement.activity_name}
                    </div>
                    <div className="pp-td col-count" role="cell">
                      <Tag value={engagement?.category?.name ?? ''}
                        appearance={TagAppearance.Blue}
                      />
                    </div>
                    <div className="pp-td col-count" role="cell">
                      <Link
                        onClick={() => {
                          setShowDescription(true)
                          setEngagementDetails(engagement)
                        }}
                      >
                        View
                      </Link>
                    </div>
                    <div className="pp-td col-count" role="cell">
                      <Tag value={engagement.status}
                        appearance={engagement.status === 'Active' ? TagAppearance.Green :
                          TagAppearance.Red}
                      />

                    </div>
                    <div className="pp-td col-count" role="cell">
                      <Tag value={engagement.is_partner_editable === 1 ? 'Editable' : 'Not Editable'}
                        appearance={engagement.is_partner_editable === 1 ? TagAppearance.Green : TagAppearance.Red}
                      />

                    </div>
                    {/* <div className="pp-td col-points region-code-badge" role="cell">
                    {region.region_code}
                  </div>
                  <div className="pp-td col-points" role="cell">
                    {Number(region.value).toLocaleString()}
                  </div> */}

                    <div className="pp-td col-actions" role="cell">
                      <IconButton
                        onClick={() => {
                          setShowEditModal(true);
                          setEngagementDetails(engagement)
                        }}
                        aria-label="Edit"
                        disabled={readOnly}
                      >
                        <Iconography icon="pen" size={IconographySizes.Small} />
                      </IconButton>
                      <IconButton
                        onClick={() => onDeleteClick(engagement.ID)}
                        aria-label="Delete"
                        disabled={readOnly}
                      >
                        <Iconography icon="trashCan" size={IconographySizes.Small} />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
        </div>
      </div>

      {showDescriptionModal &&
        <ViewDescriptionModal
          onClose={() => { setShowDescription(false) }}
          engagementDetails={engagementDetails}
        />
      }
      {deleteModalOpen && deleteTierId && (
        <DeleteModal
          setDeleteOpen={setDeleteModalOpen}
          onDeleteButtonClick={() => onDeleteEngagement(deleteTierId)}
          deleteButtonLoading={deletePartnerEngagementConfigurationListLoading}
          optionalHeader="Delete Engagement"
          message="Are you sure you want to delete this Engagement?"
        />
      )}
      {showAddModal &&
        <AddEditEngagementModal
          onClose={() => { setShowAddModal(false) }}
          engagementDetails={engagementDetails}
          onSubmit={(payload: AddEngagementDetailsType) => {

            const clean: AddEngagementDetailsType = {
              activity_name: payload.activity_name ?? "",
              category_id: payload.category_id ?? "",
              accelerator_value: payload.accelerator_value ?? [],
              is_partner_editable: payload.is_partner_editable ? 1 : 0,
              status: payload.status ? 'Active' : 'Inactive',
              comments: payload.comments ?? "",
              description: payload.description ?? "",
            };

            if (Number.isInteger(payload.category_id)) {
              dispatch(addEnagagementSagaAction(clean));
              //setShowAddModal(false);
              //dispatch(getPartnerEngagementConfigurationListSagaAction(partnerProgramId));
            } else {
              dispatch(insertEngagementCategorySagaAction(clean));
              //setShowAddModal(false);
              //dispatch(getPartnerEngagementConfigurationListSagaAction(partnerProgramId));
            }



          }}
          mode="add"
          open

        />
      }

      {showEditModal &&
        <AddEditEngagementModal
          onClose={() => { setShowEditModal(false) }}
          engagementDetails={engagementDetails}
          onSubmit={(payload: AddEngagementDetailsType) => {

            const clean: AddEngagementDetailsType = {
              activity_name: payload.activity_name ?? "",
              category_id: payload.category_id ?? "",
              accelerator_value: payload.accelerator_value ?? [],
              is_partner_editable: payload.is_partner_editable ? 1 : 0,
              status: payload.status ? 'Active' : 'Inactive',
              comments: payload.comments ?? "",
              description: payload.description ?? "",
              ID: engagementDetails.ID ?? 0,
              //ID: 0,
            };

            if (Number.isInteger(payload.category_id)) {
              dispatch(updateEnagagementSagaAction(clean));
              //setShowEditModal(false);
              //dispatch(getPartnerEngagementConfigurationListSagaAction(partnerProgramId));
            } else {
              dispatch(insertEngagementCategorySagaAction(clean));
              //setShowEditModal(false);
              //dispatch(getPartnerEngagementConfigurationListSagaAction(partnerProgramId));
            }
          }}
          mode="edit"
          open

        />
      }
      {/* {showAdd && (
        <PartnerregionMultiplierModal
          mode="add"
          open
          onClose={() => setShowAdd(false)}
          onSubmit={(payload: AddPartnerRegionalMultiplierDetailsType) => {

            const clean: AddPartnerRegionalMultiplierDetailsType = {
              ID: payload.ID,
              country: payload.country ?? "",
              region: payload.region ?? "",
              value: Number.isFinite(payload.value) ? payload.value : 0,
              region_code: payload.region_code ?? "",
            };
            if (!clean.country) {
              console.error("Country field is required.");
              return;
            }

            dispatch(addPartnerRegionalMultiplierSagaAction(clean));
            setShowAdd(false);
            dispatch(getPartnerRegionalMultiplierListSagaAction(partnerProgramId));
          }}
        />
      )} */}

      {/* {showEdit && editregion && (
        <PartnerregionMultiplierModal
          onClose={() => {
            setShowEdit(false);
            setEditregion(null);
          }}
          onSubmit={(payload: updatePartnerRegionalMultiplierDetailsType) => {
            const clean = {
              ...payload,
              ID: payload.ID,
              country: payload.country ?? "",
              region: payload.region ?? "",
              value: Number.isFinite(payload.value) ? payload.value : 0,
              region_code: payload.region_code ?? "",
            } as any;
            dispatch(updatePartnerRegionalMultiplierListSagaAction(clean));
            setShowEdit(false);
            setEditregion(null);
            dispatch(getPartnerRegionalMultiplierListSagaAction(partnerProgramId));
          }}
          mode="edit"
          open
          initialValue={{
            ID: editregion.ID,
            country: editregion.country,
            region: editregion.region,
            value: Number(editregion.value) as any,
            region_code: editregion.region_code,
          }}
        />
      )} */}
    </div>
  )
}


export default EngagementConfiguration;